/**
 * LoadingScreen Component
 *
 * Full-screen blocking loading overlay displayed during asset generation.
 * Features smooth fade transitions, accessibility support, and grayscale theme.
 *
 * @component
 * @example
 * ```tsx
 * <LoadingScreen isVisible={isGenerating} />
 * ```
 */
import React, { useState, useEffect } from 'react';
import './LoadingScreen.css';

/**
 * Props for the LoadingScreen component
 */
export interface LoadingScreenProps {
  /** Whether the loading screen is currently visible */
  isVisible: boolean;
  /** Current asset being generated (forward-compatible for Story 4.2) */
  currentAsset?: string;
  /** Generation progress percentage 0-100 (forward-compatible for Story 4.2) */
  progress?: number;
  /** Elapsed time in seconds (Story 4.2) */
  elapsedTime?: number;
  /** Show timeout warning after 20 seconds (Story 4.4) */
  timeoutWarning?: boolean;
  /** Partial failure information for demo mode fallback (Story 6.2) */
  partialFailures?: {
    failedAssets: Array<'character' | 'obstacle' | 'background'>;
    messages: string[];
  };
}

/**
 * Loading screen component with fade transitions and accessibility features
 * Story 4.2: Enhanced with sequential progress indicators, checkmarks, and elapsed time
 * Story 4.4: Added timeout warning display
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  isVisible,
  // Story 4.2: Defaults maintain Story 4.1 static behavior if props not provided
  currentAsset = 'character',
  progress = 33,
  elapsedTime = 0,
  // Story 4.4: Timeout warning (default false)
  timeoutWarning = false,
  // Story 6.2: Partial failure messages
  partialFailures,
}) => {
  const [opacity, setOpacity] = useState(0);
  // Story 6.2: Sequential message display (2 seconds per message)
  const [visibleMessageIndex, setVisibleMessageIndex] = useState(0);
  // Story 6.3: Track which messages are still visible (for 3s auto-dismiss)
  const [dismissedMessages, setDismissedMessages] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isVisible) {
      // Double RAF for smooth fade-in transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpacity(1));
      });
      // Disable body scroll when loading screen is active
      document.body.style.overflow = 'hidden';
    } else {
      // Fade out
      setOpacity(0);
      // Re-enable scroll after fade-out completes (300ms)
      setTimeout(() => {
        document.body.style.overflow = 'auto';
      }, 300);
    }
  }, [isVisible]);

  // Story 6.2: Sequential message display with 2-second intervals
  useEffect(() => {
    if (!partialFailures || partialFailures.messages.length === 0) {
      setVisibleMessageIndex(0);
      setDismissedMessages(new Set());
      return;
    }

    // Show messages sequentially with 2-second delays
    const messageInterval = setInterval(() => {
      setVisibleMessageIndex((prev) => {
        if (prev < partialFailures.messages.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 2000);

    return () => clearInterval(messageInterval);
  }, [partialFailures]);

  // Story 6.3 Code Review Fix: Auto-dismiss messages after 3 seconds (AC requirement)
  useEffect(() => {
    if (!partialFailures || partialFailures.messages.length === 0) {
      return;
    }

    // Set 3-second auto-dismiss timer for the currently visible message
    const timer = setTimeout(() => {
      setDismissedMessages((prev) => {
        const newSet = new Set(prev);
        newSet.add(visibleMessageIndex);
        return newSet;
      });
    }, 3000);

    return () => clearTimeout(timer);
  }, [visibleMessageIndex, partialFailures]);

  // Return null when not visible and fade is complete
  if (!isVisible && opacity === 0) return null;

  // Story 4.2: Helper functions for dynamic content

  /**
   * Maps asset type to step number (1, 2, or 3)
   */
  const getCurrentStep = (): number => {
    if (currentAsset === 'character') return 1;
    if (currentAsset === 'obstacle') return 2;
    return 3;
  };

  /**
   * Get estimated time based on current phase
   */
  const getEstimatedTime = (): string => {
    if (currentAsset === 'background') return 'Almost done...';
    return '~7 seconds';
  };

  /**
   * Get list of completed assets for checkmark display
   * Checkmarks appear based on currentAsset (NOT progress percentage)
   */
  const getCompletedAssets = (): string[] => {
    const completed: string[] = [];
    // Character ✓ appears when obstacle or background is current (character completed)
    if (currentAsset === 'obstacle' || currentAsset === 'background') {
      completed.push('Character ✓');
    }
    // Obstacle ✓ appears when background is current (obstacle completed)
    if (currentAsset === 'background') {
      completed.push('Obstacle ✓');
    }
    // Background ✓ only appears on final completion (progress === 100)
    if (progress === 100) {
      completed.push('Background ✓');
    }
    return completed;
  };

  // Asset name mapping with capitalization
  const assetNames: Record<string, string> = {
    character: 'Character',
    obstacle: 'Obstacle',
    background: 'Background',
  };

  // Elapsed time display: "Generated in Xs" on completion, "Xs elapsed" during progress
  const elapsedText = progress === 100
    ? `Generated in ${elapsedTime}s`
    : `${elapsedTime}s elapsed`;

  // Status text with dynamic step number
  const completedAssets = getCompletedAssets();
  const statusText = progress === 100
    ? `Complete!`
    : `Generating ${assetNames[currentAsset]}... (${getCurrentStep()}/3)`;

  return (
    <div
      className="loading-overlay"
      style={{ opacity }}
      role="dialog"
      aria-label="Loading screen"
      aria-live="polite"
    >
      <div className="loading-content">
        <div className="loading-spinner" aria-hidden="true" />
        <p className="loading-status">{statusText}</p>

        {/* Story 4.2: Progress bar with GPU-accelerated scaleX */}
        <div
          className="progress-bar-container"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="progress-bar-fill"
            style={{ transform: `scaleX(${progress / 100})` }}
          />
        </div>

        {/* Story 6.2: Partial failure messages - sequential display with 2-second intervals */}
        {/* Story 6.3 Code Review Fix: Auto-dismiss messages after 3 seconds */}
        {partialFailures && partialFailures.messages.length > 0 && (
          <div className="partial-failure-messages" aria-live="polite">
            {partialFailures.messages.slice(0, visibleMessageIndex + 1).map((message, index) => (
              !dismissedMessages.has(index) && (
                <p key={index} className="partial-failure-message">
                  {message}
                </p>
              )
            ))}
          </div>
        )}

        {/* Story 4.2: Estimated time display */}
        <p className="loading-estimate">{getEstimatedTime()}</p>

        {/* Story 4.2: Checkmarks for completed assets (always shown when there are completed assets) */}
        {completedAssets.length > 0 && (
          <p className="loading-completed">{completedAssets.join(' ')}</p>
        )}

        {/* Story 4.2: Conditional elapsed time rendering (hide when 0) */}
        {elapsedTime > 0 && (
          <p className="loading-elapsed" aria-live="polite">{elapsedText}</p>
        )}

        {/* Story 4.4: Timeout warning (displayed after 20 seconds) */}
        {timeoutWarning && (
          <div className="timeout-warning" role="alert" aria-live="polite">
            <p>Taking longer than expected...</p>
            <p className="timeout-hint">Almost done!</p>
          </div>
        )}
      </div>
    </div>
  );
};
